import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AsyncCreatableMultiselect,
  AsyncCreatableSelect,
  AsyncMultiselect,
  AsyncSelect,
  SelectOption,
  SelectOptionsOrGroups,
} from '@ardoq/select';
import { Locale } from '@ardoq/locale';
import { debounce, uniqBy } from 'lodash';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  AdditionalFilterOptionsFunc,
  FetchComponentOptionsWithQuery,
  Fields,
  GetAISuggestions,
} from '../../types';
import { GetUserFeedbackReturn } from '../getUserFeedback';
import { SurveyReadOnlyQuestion } from '../SurveyReadOnlyQuestion';
import { searchOptions } from './searchOptions';
import { ArdoqId } from '@ardoq/api-types';

type CallbackFunc = (options: SelectOptionsOrGroups<string>) => void;

type QuestionOptionsProps = {
  userFeedback: GetUserFeedbackReturn;
  surveyId: string;
  questionId: string;
  componentId: ArdoqId | undefined;
  isCreatable?: boolean;
  isMulti?: boolean;
  isDisabled?: boolean;
  value?: any;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  isReadonly?: boolean;
  fields?: Fields;
  identifierFieldName: string | undefined;
  preventReferenceModification?: boolean | null;
  shouldFetchReferenceSuggestions?: boolean;
  startingValue?: string;
  questionLabel: string;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  onChange: (
    options: SelectOption<string>[] | SelectOption<string> | null
  ) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
};

export const SurveyQuestionOptions = ({
  userFeedback,
  surveyId,
  questionId,
  componentId,
  isCreatable,
  isMulti,
  value,
  isDisabled = false,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  isReadonly,
  fields = {},
  identifierFieldName,
  preventReferenceModification,
  shouldFetchReferenceSuggestions = false,
  startingValue,
  questionLabel,
  onChange,
  additionalFilterOptions,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
}: QuestionOptionsProps) => {
  const [status, setStatus] = useState<'idle' | 'loading'>('loading');
  const [lastOptions, setLastOptions] = useState<SelectOptionsOrGroups<string>>(
    []
  );

  const searchAndLoad = (query: string, callback: CallbackFunc) => {
    setStatus('loading');
    searchOptions({
      surveyId,
      questionId,
      componentId,
      query,
      currentLocale,
      fields,
      identifierFieldName,
      shouldFetchReferenceSuggestions,
      getAISuggestions,
      fetchComponentOptionsWithQuery,
      i18nLocale,
    }).then(response => {
      const startingOption = startingValue
        ? [{ label: startingValue, value }]
        : [];
      const loadedOptionsWithStartingOption = uniqBy(
        [...response, ...startingOption],
        'value'
      );
      callback(loadedOptionsWithStartingOption);
      setLastOptions(loadedOptionsWithStartingOption);
      setStatus('idle');
    });
  };
  const searchAndLoadRef = useRef(searchAndLoad);
  useEffect(() => {
    searchAndLoadRef.current = searchAndLoad;
  });

  const loadOptions = useMemo(
    () =>
      debounce(
        (query: string, callback: CallbackFunc) =>
          searchAndLoadRef.current(query, callback),
        300
      ),
    []
  );

  useEffect(() => () => loadOptions.cancel(), [loadOptions]);

  const commonProps = {
    onChange,
    errorMessage: userFeedback?.error,
    warningMessage: userFeedback?.warning,
    value:
      typeof value === 'string'
        ? lastOptions &&
          lastOptions.filter(
            option => 'value' in option && option.value === value
          )
        : value,
    isClearable: !isMulti,
    defaultOptions: true,
    loadOptions,
    isDisabled: isDisabled || Boolean(preventReferenceModification),
    filterOption: additionalFilterOptions,
    i18nLocale,
    placeholder:
      status === 'idle'
        ? t(i18nLocale, 'surveysUi:options.typeToSearch', 'Type to search')
        : t(i18nLocale, 'surveysUi:options.loading', 'Loading...'),
    maxMenuHeight: 300,
  };

  if (isReadonly && commonProps.value?.length)
    return (
      <SurveyReadOnlyQuestion i18nLocale={i18nLocale}>
        {commonProps.value
          .map((selection: SelectOption<string>) => selection.label)
          .join(', ')}
      </SurveyReadOnlyQuestion>
    );

  if (isCreatable) {
    return isMulti ? (
      <AsyncCreatableMultiselect
        {...commonProps}
        placeholder={t(
          i18nLocale,
          'surveysUi:options.typeToCreate',
          'Type to create'
        )}
        dataTestId="async-creatable-multiselect"
        aria-label={questionLabel}
      />
    ) : (
      <AsyncCreatableSelect
        {...commonProps}
        placeholder={t(
          i18nLocale,
          'surveysUi:options.typeToSelect',
          'Type to select'
        )}
        dataTestId="async-creatable-select"
        aria-label={questionLabel}
      />
    );
  }

  return isMulti ? (
    <AsyncMultiselect
      {...commonProps}
      hideSelectedOptions
      dataTestId="async-multiselect"
      aria-label={questionLabel}
    />
  ) : (
    <AsyncSelect
      {...commonProps}
      dataTestId="async-select"
      aria-label={questionLabel}
    />
  );
};
